import { OverflowList } from '@theme/critical';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, requestYieldCallback } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];
  #deferredUnmountTimeout;
  /** @type {IntersectionObserver | null} */
  #deferredViewportObserver = null;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #compareVisibilityRetryTimeout;
  #compareVisibilityRetries = 0;
  /** @type {WeakSet<Element>} */
  #compareWidgets = new WeakSet();
  /** @type {WeakSet<HTMLInputElement>} */
  #compareInputs = new WeakSet();
  /** @type {Array<() => void>} */
  #compareVisibilityCleanups = [];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    this.#updateOverflowList();
    this.#handleCompareVisibilityBreakpoint();
    this.#handleDeferredSlideshowViewport();
    mediaQueryLarge.addEventListener('change', this.#handleDeferredSlideshowViewport);
    mediaQueryLarge.addEventListener('change', this.#handleCompareVisibilityBreakpoint);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    clearTimeout(this.#deferredUnmountTimeout);
    this.#cleanupCompareVisibility();
    this.#disconnectDeferredSlideshowViewportObserver();
    mediaQueryLarge.removeEventListener('change', this.#handleDeferredSlideshowViewport);
    mediaQueryLarge.removeEventListener('change', this.#handleCompareVisibilityBreakpoint);
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Keeps checked compare buttons visible on desktop without observing the whole card DOM.
   */
  #handleCompareVisibilityBreakpoint = () => {
    if (!isDesktopBreakpoint()) {
      this.#cleanupCompareVisibility();
      return;
    }

    this.#handleCompareVisibility();
  };

  #handleCompareVisibility = () => {
    if (!isDesktopBreakpoint()) return;

    const compareWidgets = this.querySelectorAll('sc-add-to-compare');
    let hasPendingWidget = false;

    for (const widget of compareWidgets) {
      if (!this.#connectCompareWidget(widget)) {
        hasPendingWidget = true;
      }
    }

    this.querySelectorAll('.sc-app-block').forEach((appBlock) => this.#syncCompareAppBlock(appBlock));

    if (!hasPendingWidget) {
      this.#compareVisibilityRetries = 0;
      clearTimeout(this.#compareVisibilityRetryTimeout);
      return;
    }

    if (this.#compareVisibilityRetries >= 40) return;

    clearTimeout(this.#compareVisibilityRetryTimeout);
    this.#compareVisibilityRetries += 1;
    this.#compareVisibilityRetryTimeout = setTimeout(this.#handleCompareVisibility, 250);
  };

  /**
   * @param {Element} widget
   * @returns {boolean}
   */
  #connectCompareWidget(widget) {
    if (!this.#compareWidgets.has(widget)) {
      const handleWidgetEvent = () => {
        setTimeout(() => {
          const appBlock = widget.closest('.sc-app-block');
          if (appBlock) this.#syncCompareAppBlock(appBlock);
        });
      };

      widget.addEventListener('click', handleWidgetEvent);
      widget.addEventListener('change', handleWidgetEvent);
      this.#compareWidgets.add(widget);
      this.#compareVisibilityCleanups.push(() => {
        widget.removeEventListener('click', handleWidgetEvent);
        widget.removeEventListener('change', handleWidgetEvent);
      });
    }

    const input = widget.shadowRoot?.querySelector('input[name="add_to_compare"][type="checkbox"]');
    if (!(input instanceof HTMLInputElement)) return false;

    if (!this.#compareInputs.has(input)) {
      const handleInputEvent = () => {
        const appBlock = widget.closest('.sc-app-block');
        if (appBlock) this.#syncCompareAppBlock(appBlock);
      };

      input.addEventListener('input', handleInputEvent);
      input.addEventListener('change', handleInputEvent);
      this.#compareInputs.add(input);
      this.#compareVisibilityCleanups.push(() => {
        input.removeEventListener('input', handleInputEvent);
        input.removeEventListener('change', handleInputEvent);
      });
    }

    const appBlock = widget.closest('.sc-app-block');
    if (appBlock) this.#syncCompareAppBlock(appBlock);

    return true;
  }

  /**
   * @param {Element} appBlock
   */
  #syncCompareAppBlock(appBlock) {
    const hasCheckedCompare = Array.from(appBlock.querySelectorAll('sc-add-to-compare')).some((widget) => {
      const input = widget.shadowRoot?.querySelector('input[name="add_to_compare"][type="checkbox"]');
      return input instanceof HTMLInputElement && input.checked;
    });

    appBlock.toggleAttribute('data-compare-checked', hasCheckedCompare);
  }

  #cleanupCompareVisibility() {
    clearTimeout(this.#compareVisibilityRetryTimeout);
    this.#compareVisibilityRetries = 0;

    for (const cleanup of this.#compareVisibilityCleanups) cleanup();
    this.#compareVisibilityCleanups = [];
    this.#compareWidgets = new WeakSet();
    this.#compareInputs = new WeakSet();

    this.querySelectorAll('.sc-app-block[data-compare-checked]').forEach((appBlock) => {
      appBlock.removeAttribute('data-compare-checked');
    });
  }

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateCardGallery(event);
    this.querySelectorAll('quick-add-component[stay-visible]').forEach((quickAdd) => {
      quickAdd.removeAttribute('stay-visible');
    });

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
    this.#handleCompareVisibilityBreakpoint();
  };

  /**
   * Updates the card gallery DOM from the HTML returned by the variant update.
   * This ensures the slideshow reflects the currently selected variant (e.g. different media ordering/filtering).
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateCardGallery(event) {
    const currentCardGallery = this.refs.cardGallery;
    if (!(currentCardGallery instanceof HTMLElement)) return;

    const currentProductId = currentCardGallery.dataset.productId;

    let newCardGallery = /** @type {HTMLElement | null} */ (
      currentProductId
        ? event.detail.data.html?.querySelector(`[ref="cardGallery"][data-product-id="${currentProductId}"]`)
        : null
    );

    // Combined listings can switch the underlying product, so the old product ID might not exist in new HTML.
    if (!(newCardGallery instanceof HTMLElement) && event.detail.data.newProduct?.id) {
      newCardGallery = /** @type {HTMLElement | null} */ (
        event.detail.data.html?.querySelector(`[ref="cardGallery"][data-product-id="${event.detail.data.newProduct.id}"]`)
      );
    }

    if (!(newCardGallery instanceof HTMLElement)) {
      newCardGallery = /** @type {HTMLElement | null} */ (event.detail.data.html?.querySelector('[ref="cardGallery"]'));
    }

    if (!(newCardGallery instanceof HTMLElement)) return;

    const newDeferredSlideshowTemplate = newCardGallery.querySelector('[ref="deferredSlideshowTemplate"]');
    const newPreviewTemplate = newCardGallery.querySelector('[ref="cardGalleryPreviewTemplate"]');

    morph(currentCardGallery, newCardGallery);

    // Keep template contents in sync after morphing.
    // template.content is not guaranteed to be reconciled by DOM morphing.
    const deferredSlideshowTemplate = currentCardGallery.querySelector('[ref="deferredSlideshowTemplate"]');
    const previewTemplate = currentCardGallery.querySelector('[ref="cardGalleryPreviewTemplate"]');

    if (
      deferredSlideshowTemplate instanceof HTMLTemplateElement &&
      newDeferredSlideshowTemplate instanceof HTMLTemplateElement
    ) {
      deferredSlideshowTemplate.innerHTML = newDeferredSlideshowTemplate.innerHTML;
    }

    if (previewTemplate instanceof HTMLTemplateElement && newPreviewTemplate instanceof HTMLTemplateElement) {
      previewTemplate.innerHTML = newPreviewTemplate.innerHTML;
    }

    const mountPoint = currentCardGallery.querySelector('[ref="deferredSlideshowMount"]');
    if (!(mountPoint instanceof HTMLElement) || !(deferredSlideshowTemplate instanceof HTMLTemplateElement)) return;

    const hasMountedSlideshow = mountPoint.querySelector('slideshow-component');

    // If slideshow is currently mounted (e.g. desktop hover), replace it with the updated variant markup.
    if (hasMountedSlideshow) {
      mountPoint.replaceChildren(deferredSlideshowTemplate.content.cloneNode(true));
      return;
    }

    // On mobile we keep the slideshow mounted for swipe support.
    if (!isDesktopBreakpoint()) {
      mountPoint.replaceChildren(deferredSlideshowTemplate.content.cloneNode(true));
    }
  }

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    if (!overflowList) return;

    const triggerReflow = () => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    };

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // If the component is not yet defined, wait for it
      if (!customElements.get('overflow-list')) {
        customElements.whenDefined('overflow-list').then(triggerReflow);
      } else {
        triggerReflow();
      }
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      const hasVariantImageSlides = slides.some((slide) => slide.getAttribute('variant-image') != null);
      if (hasVariantImageSlides) {
        for (const slide of slides) {
          if (slide.getAttribute('variant-image') == null) continue;
          slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
        }
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    this.#ensureDeferredSlideshowMounted();
    const slideshow = this.refs.slideshow ?? this.querySelector('slideshow-component');

    if (!slideshow || typeof slideshow.select !== 'function') return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;
    this.#ensureDeferredSlideshowMounted();

    const slideshow = this.refs.slideshow ?? this.querySelector('slideshow-component');

    if (!slideshow || typeof slideshow.select !== 'function') return;

    this.resetVariant.cancel();

    // Product-card gallery hover behavior: always show the 2nd slide immediately (if present),
    // then allow the user to continue navigating with slideshow controls.
    if ((slideshow.slides?.length || 0) < 2) return;

    slideshow.select(1, undefined, { animate: false });
    setTimeout(() => this.#preloadNextPreviewImage());
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the slideshow to the first image when leaving the gallery area.
   * Used by product card galleries on collection pages.
   * @param {PointerEvent} event - The pointer event.
   */
  resetToFirstImage(event) {
    if (event.pointerType !== 'mouse') return;

    const slideshow = this.refs.slideshow ?? this.querySelector('slideshow-component');
    if (!slideshow || typeof slideshow.select !== 'function') return;

    this.resetVariant.cancel();
    this.#previousSlideIndex = null;

    slideshow.select(0, undefined, { animate: false });
  }

  /**
   * Mounts deferred card-gallery slideshow markup when needed.
   * @returns {boolean}
   */
  #ensureDeferredSlideshowMounted() {
    if (this.refs.slideshow) return true;

    const mountPoint = this.querySelector('[ref="deferredSlideshowMount"]');
    const slideshowTemplate = this.querySelector('[ref="deferredSlideshowTemplate"]');
    if (!(mountPoint instanceof HTMLElement) || !(slideshowTemplate instanceof HTMLTemplateElement)) return false;

    clearTimeout(this.#deferredUnmountTimeout);

    if (mountPoint.children.length === 0 || !mountPoint.querySelector('slideshow-component')) {
      mountPoint.replaceChildren(slideshowTemplate.content.cloneNode(true));
    }

    return true;
  }

  /**
   * Restores static preview markup and removes slideshow DOM.
   */
  #teardownDeferredSlideshow() {
    const mountPoint = this.querySelector('[ref="deferredSlideshowMount"]');
    const previewTemplate = this.querySelector('[ref="cardGalleryPreviewTemplate"]');
    if (!(mountPoint instanceof HTMLElement) || !(previewTemplate instanceof HTMLTemplateElement)) return;

    mountPoint.replaceChildren(previewTemplate.content.cloneNode(true));
    this.#previousSlideIndex = null;
  }

  /**
   * Handles deferred gallery behavior across viewport changes.
   * On mobile, mount deferred slider near viewport for immediate swipe support.
   * On desktop, keep lazy hover mounting behavior.
   */
  #handleDeferredSlideshowViewport = () => {
    if (!this.#isForceDeferredGallery()) return;

    if (isDesktopBreakpoint()) {
      this.#disconnectDeferredSlideshowViewportObserver();
      this.#teardownDeferredSlideshow();
      return;
    }

    this.#observeDeferredSlideshowViewport();
  };

  /**
   * Schedules deferred slideshow teardown to keep card DOM lightweight.
   * @param {number} delayMs
   */
  #scheduleDeferredSlideshowTeardown(delayMs) {
    clearTimeout(this.#deferredUnmountTimeout);
    this.#deferredUnmountTimeout = setTimeout(() => {
      if (!this.isConnected) return;
      this.#teardownDeferredSlideshow();
    }, delayMs);
  }

  #isForceDeferredGallery() {
    return this.refs.cardGallery?.dataset.forceDeferredGallery === 'true';
  }

  #disconnectDeferredSlideshowViewportObserver() {
    this.#deferredViewportObserver?.disconnect();
    this.#deferredViewportObserver = null;
  }

  #isIOSWebKit() {
    const ua = window.navigator.userAgent || '';
    const isIOSDevice =
      /iP(hone|ad|od)/.test(ua) || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
    const isAppleWebKit = /WebKit/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
    return isIOSDevice && isAppleWebKit;
  }

  #observeDeferredSlideshowViewport() {
    if (this.#deferredViewportObserver || !this.#isForceDeferredGallery()) return;

    const cardGallery = this.refs.cardGallery;
    if (!(cardGallery instanceof HTMLElement)) return;
    const isIOSWebKit = this.#isIOSWebKit();
    const rootMargin = isIOSWebKit ? '60px 0px 80px 0px' : '260px 0px 320px 0px';
    const threshold = isIOSWebKit ? 0.08 : 0.01;
    const teardownDelay = isIOSWebKit ? 40 : 180;

    this.#deferredViewportObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#ensureDeferredSlideshowMounted();
            continue;
          }

          this.#scheduleDeferredSlideshowTeardown(teardownDelay);
        }
      },
      {
        rootMargin,
        threshold,
      }
    );

    this.#deferredViewportObserver.observe(cardGallery);
  }

  /**
   * Mounts slideshow on hover and keeps existing hover preview behavior.
   * @param {PointerEvent} event
   */
  activateDeferredSlideshow(event) {
    const pointerType = event.pointerType;
    const isMouse = pointerType === 'mouse';
    const isTouchLike = pointerType === 'touch' || pointerType === 'pen';

    if (!isMouse && !isTouchLike) return;

    clearTimeout(this.#deferredUnmountTimeout);
    const mounted = this.#ensureDeferredSlideshowMounted();
    if (!mounted) {
      return;
    }

    if (isTouchLike) return;

    queueMicrotask(() => {
      if (!this.isConnected) return;
      this.previewImage(event);
    });
  }

  /**
   * Resets to first image and removes slideshow DOM after leaving the gallery.
   * @param {PointerEvent} event
   */
  deactivateDeferredSlideshow(event) {
    if (event.pointerType !== 'mouse') return;

    this.resetToFirstImage(event);
    this.#scheduleDeferredSlideshowTeardown(60);
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 *
 * @typedef {object} SwatchesRefs
 * @property {HTMLElement} overflowList
 *
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
